<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { useRoute } from 'vue-router'
import { navFor, PATHS, settings } from '@/content'
import { isProPath } from '@/lib/section'
import type { NavItem } from '@/types'

const route = useRoute()
const isPro = computed(() => isProPath(route.path))
// Menu affiche : celui de l'Espace Professionnels sous /pro/, celui des Particuliers ailleurs.
const menu = computed(() => navFor(route.path))
// Sous /pro/, le logo ramene a l'accueil professionnels (le retour BtoC est dans le bandeau).
const homePath = computed(() => (isPro.value ? PATHS.pro : '/'))
const mobileOpen = ref(false)
const openAccordion = ref<string | null>(null)
const hovered = ref<string | null>(null)
const flipped = ref<Set<string>>(new Set())

// Familles de services (avec sous-menu) dans la barre principale ;
// liens utilitaires (sans sous-menu : Contact, Blog, Qui sommes-nous) dans la barre haute.
const families = computed(() => menu.value.filter((i) => i.children?.length))
const utilityLinks = computed(() => menu.value.filter((i) => !i.children?.length))

function isCta(item: NavItem) {
  return item.label.trim().toLowerCase() === 'dépannage'
}

// Ouvre le mega-menu et le retourne vers la gauche s'il deborderait a droite (items de droite).
function onEnter(item: NavItem, ev: MouseEvent) {
  hovered.value = item.label
  if (!item.children?.length) return
  nextTick(() => {
    const li = ev.currentTarget as HTMLElement | null
    const mega = li?.querySelector('.mega') as HTMLElement | null
    if (!li || !mega) return
    const rect = li.getBoundingClientRect()
    const overflowsRight = rect.left + mega.offsetWidth > window.innerWidth - 12
    const next = new Set(flipped.value)
    if (overflowsRight) next.add(item.label)
    else next.delete(item.label)
    flipped.value = next
  })
}
function toggleAccordion(label: string) {
  openAccordion.value = openAccordion.value === label ? null : label
}

// Ferme le menu mobile a chaque navigation.
watch(
  () => route.fullPath,
  () => {
    mobileOpen.value = false
    hovered.value = null
  },
)
</script>

<template>
  <header class="hdr" :class="{ 'hdr--pro': isPro }">
    <!-- Bandeau Espace Professionnels (theme sombre) -->
    <div v-if="isPro" class="hdr-pro">
      <div class="container hdr-pro__in">
        <span class="hdr-pro__tag">Espace Professionnels</span>
        <router-link to="/" class="hdr-pro__back">
          <span aria-hidden="true">&larr;</span> Retour au site Particuliers
        </router-link>
      </div>
    </div>

    <!-- Barre haute (desktop) -->
    <div class="hdr-top">
      <div class="container hdr-top__in">
        <div class="hdr-top__left">
          <span class="hdr-top__baseline">{{ settings.baseline }}</span>
          <nav class="hdr-top__nav" aria-label="Liens utiles">
            <router-link
              v-for="u in utilityLinks"
              :key="u.path"
              :to="u.path"
              class="hdr-top__util"
            >{{ u.label }}</router-link>
            <router-link v-if="!isPro" :to="PATHS.pro" class="hdr-top__util hdr-top__pro">
              Espace Pro
            </router-link>
          </nav>
        </div>
        <div class="hdr-top__contact">
          <a :href="`mailto:${settings.email}`" class="hdr-top__link">
            <span class="ico" aria-hidden="true">✉</span>{{ settings.email }}
          </a>
          <a
            v-for="p in settings.phones"
            :key="p.tel"
            :href="`tel:${p.tel}`"
            class="hdr-top__link"
          >
            <span class="ico ico--green" aria-hidden="true">✆</span>{{ p.display }}
            <span class="hdr-top__city">- {{ p.label }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Barre principale -->
    <div class="hdr-main">
      <div class="container hdr-main__in">
        <router-link :to="homePath" class="hdr-logo" aria-label="Accueil - Les Ateliers du Terras">
          <img
            src="/opt/logo-400.webp"
            srcset="/opt/logo-200.webp 200w, /opt/logo-400.webp 400w"
            sizes="143px"
            width="200"
            height="84"
            alt="Les Ateliers du Terras"
            fetchpriority="high"
          />
        </router-link>

        <!-- Navigation desktop -->
        <nav class="hdr-nav" aria-label="Navigation principale">
          <ul class="hdr-nav__list">
            <li
              v-for="item in families"
              :key="item.path"
              class="hdr-nav__item"
              :class="{ 'has-mega': item.children?.length }"
              @mouseenter="onEnter(item, $event)"
              @mouseleave="hovered = null"
            >
              <router-link
                :to="item.path"
                class="hdr-nav__link"
                :class="{ 'is-cta': isCta(item) }"
              >
                {{ item.label }}
                <span v-if="item.children?.length" class="caret" aria-hidden="true">▾</span>
              </router-link>

              <div
                v-if="item.children?.length"
                class="mega"
                :class="{ open: hovered === item.label, 'mega--flip': flipped.has(item.label) }"
              >
                <div class="mega__in">
                  <div v-for="child in item.children" :key="child.path" class="mega__col">
                    <router-link :to="child.path" class="mega__head">{{ child.label }}</router-link>
                    <ul v-if="child.children?.length" class="mega__sub">
                      <li v-for="g in child.children" :key="g.path">
                        <router-link :to="g.path">{{ g.label }}</router-link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </nav>

        <!-- Burger mobile -->
        <button
          class="hdr-burger"
          :aria-expanded="mobileOpen"
          aria-label="Ouvrir le menu"
          @click="mobileOpen = !mobileOpen"
        >
          <span :class="{ open: mobileOpen }"></span>
        </button>
      </div>
    </div>

    <!-- Menu mobile plein ecran -->
    <transition name="slide">
      <div v-if="mobileOpen" class="mnav">
        <div class="mnav__contact">
          <a
            v-for="p in settings.phones"
            :key="p.tel"
            :href="`tel:${p.tel}`"
            class="btn btn--primary btn--block"
          >
            Appeler {{ p.label }} : {{ p.display }}
          </a>
        </div>
        <ul class="mnav__list">
          <li v-for="item in menu" :key="item.path" class="mnav__item">
            <div class="mnav__row">
              <router-link :to="item.path" class="mnav__link">{{ item.label }}</router-link>
              <button
                v-if="item.children?.length"
                class="mnav__toggle"
                :aria-expanded="openAccordion === item.label"
                :aria-label="`Deployer ${item.label}`"
                @click="toggleAccordion(item.label)"
              >
                <span :class="{ open: openAccordion === item.label }">▾</span>
              </button>
            </div>
            <ul
              v-if="item.children?.length"
              class="mnav__children"
              :class="{ open: openAccordion === item.label }"
            >
              <li v-for="child in item.children" :key="child.path">
                <router-link :to="child.path">{{ child.label }}</router-link>
                <ul v-if="child.children?.length" class="mnav__sub">
                  <li v-for="g in child.children" :key="g.path">
                    <router-link :to="g.path">{{ g.label }}</router-link>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
        <!-- Bascule entre les deux univers, toujours accessible sur mobile. -->
        <router-link v-if="!isPro" :to="PATHS.pro" class="mnav__switch">
          Espace Professionnels <span aria-hidden="true">&rarr;</span>
        </router-link>
        <router-link v-else to="/" class="mnav__switch">
          <span aria-hidden="true">&larr;</span> Retour au site Particuliers
        </router-link>
        <a :href="`mailto:${settings.email}`" class="mnav__email">{{ settings.email }}</a>
      </div>
    </transition>
  </header>
</template>

<style scoped>
.hdr {
  position: sticky;
  top: 0;
  z-index: 100;
  background: #fff;
  box-shadow: 0 1px 0 var(--border);
}
.hdr-top {
  background: #fff;
  border-bottom: 1px solid var(--border);
  font-size: 0.82rem;
}
.hdr-top__in {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 40px;
  gap: 16px;
}
.hdr-top__left {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}
.hdr-top__baseline {
  color: var(--green-dark);
  font-weight: 700;
  white-space: nowrap;
}
.hdr-top__nav {
  display: flex;
  gap: 16px;
  padding-left: 18px;
  border-left: 1px solid var(--border);
}
.hdr-top__util {
  color: var(--ink);
  font-weight: 600;
  white-space: nowrap;
}
.hdr-top__util:hover,
.hdr-top__util.router-link-active {
  color: var(--green-dark);
  text-decoration: none;
}
/* Bascule vers l'univers professionnel : signalee sans crier plus fort que le reste. */
.hdr-top__pro {
  color: var(--green-dark);
  padding-left: 16px;
  border-left: 1px solid var(--border);
}
.hdr-top__pro::before {
  content: '▸';
  margin-right: 5px;
  color: var(--green);
}
@media (max-width: 980px) {
  .hdr-top__nav { display: none; }
}
.hdr-top__contact {
  display: flex;
  gap: 22px;
  flex-wrap: nowrap;
}
/* Avec les liens utiles, l'accroche ne tient plus dans le conteneur et faisait
   chevaucher ou passer a la ligne les coordonnees : elle n'est gardee que lorsque
   ces liens sont masques (les villes restent lisibles a cote des telephones). */
@media (min-width: 981px) {
  .hdr-top__baseline { display: none; }
  .hdr-top__nav { padding-left: 0; border-left: 0; }
}
.hdr-top__link {
  color: var(--ink);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.hdr-top__link:hover {
  color: var(--green-dark);
  text-decoration: none;
}
.hdr-top__city {
  color: var(--muted);
}
.ico {
  color: var(--muted);
}
.ico--green {
  color: var(--green);
}

.hdr-main__in {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 74px;
}
.hdr-logo img {
  width: auto;
  height: 60px;
}

.hdr-nav__list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 2px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.hdr-nav__item {
  position: relative;
}
.hdr-nav__link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 10px 7px;
  color: var(--title);
  font-family: var(--font-title);
  font-weight: 700;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0;
  border-radius: var(--radius-sm);
  white-space: nowrap;
}
@media (min-width: 1041px) {
  .hdr-nav__list {
    flex-wrap: nowrap;
  }
}
/* Ecrans desktop intermediaires : resserre pour que les 8 familles tiennent sur une ligne. */
@media (max-width: 1400px) and (min-width: 1041px) {
  .hdr-nav__link {
    padding: 10px 5px;
    font-size: 0.72rem;
    gap: 2px;
  }
  .hdr-logo img {
    height: 50px;
  }
  .hdr-main__in {
    gap: 12px;
  }
}
.hdr-nav__link:hover {
  color: var(--green-dark);
  text-decoration: none;
}
.hdr-nav__link.is-cta {
  background: var(--green);
  color: #272a23;
}
.hdr-nav__link.is-cta:hover {
  background: var(--green-hover);
  color: #272a23;
}
.caret {
  font-size: 0.6rem;
  color: var(--green);
}

.mega {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 260px;
  background: #fff;
  border: 1px solid var(--border);
  border-top: 3px solid var(--green);
  border-radius: 0 0 var(--radius) var(--radius);
  box-shadow: var(--shadow);
  padding: 16px 20px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  transition: opacity 0.15s, transform 0.15s, visibility 0.15s;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px 26px;
  max-width: min(760px, 90vw);
}
.mega.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}
/* Items de droite : le mega s'ouvre vers la gauche pour rester dans l'ecran. */
.mega--flip {
  left: auto;
  right: 0;
}
.mega__in {
  display: contents;
}
.mega__col {
  min-width: 200px;
}
.mega__head {
  display: block;
  font-family: var(--font-title);
  font-weight: 700;
  color: var(--title);
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
  margin-bottom: 6px;
}
.mega__head:hover {
  color: var(--green-dark);
  text-decoration: none;
}
.mega__sub {
  list-style: none;
  margin: 0;
  padding: 0;
}
.mega__sub li a {
  display: block;
  padding: 4px 0;
  color: var(--ink);
  font-size: 0.9rem;
}
.mega__sub li a:hover {
  color: var(--green-dark);
  text-decoration: none;
  padding-left: 4px;
}

/* Burger + menu mobile */
.hdr-burger {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: #fff;
  cursor: pointer;
  position: relative;
}
.hdr-burger span,
.hdr-burger span::before,
.hdr-burger span::after {
  content: '';
  position: absolute;
  left: 11px;
  width: 22px;
  height: 2.5px;
  background: var(--title);
  border-radius: 2px;
  transition: transform 0.2s, opacity 0.2s;
}
.hdr-burger span {
  top: 21px;
}
.hdr-burger span::before {
  top: -7px;
}
.hdr-burger span::after {
  top: 7px;
}
.hdr-burger span.open {
  background: transparent;
}
.hdr-burger span.open::before {
  transform: translateY(7px) rotate(45deg);
}
.hdr-burger span.open::after {
  transform: translateY(-7px) rotate(-45deg);
}

.mnav {
  position: fixed;
  top: var(--header-h);
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  overflow-y: auto;
  padding: 18px 18px 80px;
  z-index: 99;
}
.mnav__contact {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}
.mnav__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.mnav__item {
  border-bottom: 1px solid var(--border);
}
.mnav__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mnav__link {
  display: block;
  padding: 14px 4px;
  font-family: var(--font-title);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.95rem;
  color: var(--title);
  flex: 1;
}
.mnav__toggle {
  width: 48px;
  height: 48px;
  border: none;
  background: transparent;
  color: var(--green);
  font-size: 1rem;
  cursor: pointer;
}
.mnav__toggle span {
  display: inline-block;
  transition: transform 0.2s;
}
.mnav__toggle span.open {
  transform: rotate(180deg);
}
.mnav__children {
  display: none;
  list-style: none;
  margin: 0 0 10px;
  padding: 0 0 0 12px;
}
.mnav__children.open {
  display: block;
}
.mnav__children > li > a {
  display: block;
  padding: 9px 4px;
  color: var(--ink);
  font-weight: 600;
}
.mnav__sub {
  list-style: none;
  padding: 0 0 6px 14px;
  margin: 0;
}
.mnav__sub li a {
  display: block;
  padding: 6px 4px;
  color: var(--muted);
  font-size: 0.92rem;
}
.mnav__switch {
  display: block;
  margin-top: 18px;
  padding: 12px 14px;
  border: 1px solid var(--green);
  border-radius: var(--radius-sm);
  text-align: center;
  font-family: var(--font-title);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.85rem;
  color: var(--green-dark);
}
.mnav__switch:hover {
  text-decoration: none;
  background: var(--green);
  color: #272a23;
}
.mnav__email {
  display: block;
  margin-top: 18px;
  color: var(--green-dark);
  font-weight: 600;
}

.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.2s;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
}

@media (max-width: 1040px) {
  .hdr-nav {
    display: none;
  }
  .hdr-burger {
    display: block;
  }
}
@media (max-width: 720px) {
  .hdr-top {
    display: none;
  }
  :root {
    --header-h: 74px;
  }
  .hdr-logo img {
    height: 46px;
  }
}

/* ===== Theme sombre PRO ===== */
.hdr-pro {
  background: #1c1e18;
  border-bottom: 1px solid #3a3e33;
}
.hdr-pro__in {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 34px;
  flex-wrap: wrap;
}
.hdr-pro__tag {
  color: var(--green);
  font-family: var(--font-title);
  font-weight: 800;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.hdr-pro__back {
  color: #e8ebe0;
  font-size: 0.8rem;
  font-weight: 600;
}
.hdr-pro__back:hover {
  color: var(--green);
  text-decoration: none;
}

.hdr--pro {
  background: #272a23;
  box-shadow: 0 1px 0 #3a3e33;
}
.hdr--pro .hdr-top {
  background: #272a23;
  border-bottom-color: #3a3e33;
}
.hdr--pro .hdr-top__baseline {
  color: var(--green);
}
.hdr--pro .hdr-top__link {
  color: #e8ebe0;
}
.hdr--pro .hdr-top__link:hover {
  color: var(--green);
}
.hdr--pro .hdr-top__util {
  color: #e8ebe0;
}
.hdr--pro .hdr-top__util:hover {
  color: var(--green);
}
.hdr--pro .hdr-top__nav {
  border-left-color: #3a3e33;
}
.hdr--pro .hdr-top__city,
.hdr--pro .ico {
  color: #a7ac9c;
}
.hdr--pro .ico--green {
  color: var(--green);
}
.hdr--pro .hdr-main__in {
  background: #272a23;
}
.hdr--pro .hdr-nav__link {
  color: #f0f2ea;
}
.hdr--pro .hdr-nav__link:hover {
  color: var(--green);
}
.hdr--pro .mega {
  background: #2f332b;
  border-color: #3a3e33;
}
.hdr--pro .mega__head {
  color: #f0f2ea;
  border-bottom-color: #3a3e33;
}
.hdr--pro .mega__head:hover,
.hdr--pro .mega__sub li a:hover {
  color: var(--green);
}
.hdr--pro .mega__sub li a {
  color: #c4c9ba;
}
.hdr--pro .hdr-burger {
  background: #2f332b;
  border-color: #3a3e33;
}
.hdr--pro .hdr-burger span,
.hdr--pro .hdr-burger span::before,
.hdr--pro .hdr-burger span::after {
  background: #f0f2ea;
}
.hdr--pro .hdr-burger span.open {
  background: transparent;
}
.hdr--pro .mnav {
  background: #272a23;
}
.hdr--pro .mnav__link {
  color: #f0f2ea;
}
.hdr--pro .mnav__item {
  border-bottom-color: #3a3e33;
}
.hdr--pro .mnav__children > li > a {
  color: #d6dbcb;
}
.hdr--pro .mnav__sub li a {
  color: #a7ac9c;
}
.hdr--pro .mnav__email,
.hdr--pro .mnav__switch {
  color: var(--green);
}
.hdr--pro .mnav__switch:hover {
  color: #272a23;
}
</style>
